import { gql, useMutation } from '@apollo/client';
import { useState } from 'react';

import type { ProcessorUpgradeFeature } from '@sb/service-interfaces/host/types';

const REQUEST_PROCESSOR_UPGRADE_MUTATION = gql`
  mutation requestProcessorUpgrade($input: RequestProcessorUpgradeInput!) {
    requestProcessorUpgrade(input: $input) {
      success
      error
    }
  }
`;

export type ProcessorUpgradeOutcome =
  | { kind: 'sent' }
  | { kind: 'failed'; error: string | null };

/**
 * Raises an upgrade request for this control box. The customer, its company
 * record and its account contacts are resolved server-side from the calling
 * robot, so nothing identifying is sent from here — only which feature was
 * locked and what the processor is.
 */
export function useRequestProcessorUpgrade() {
  const [requestUpgrade] = useMutation(REQUEST_PROCESSOR_UPGRADE_MUTATION);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitUpgradeRequest = async (
    input: {
      feature: ProcessorUpgradeFeature;
      processorSummary?: string;
    },
    onOutcome: (outcome: ProcessorUpgradeOutcome) => void,
  ): Promise<void> => {
    try {
      setIsSubmitting(true);

      const { data } = await requestUpgrade({ variables: { input } });
      const result = data?.requestProcessorUpgrade;

      onOutcome(
        result?.success
          ? { kind: 'sent' }
          : { kind: 'failed', error: result?.error ?? null },
      );
    } catch (err) {
      onOutcome({
        kind: 'failed',
        error: err instanceof Error ? err.message : null,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submitUpgradeRequest, isSubmitting };
}
